import { Injectable } from '@angular/core';
import { doc, DocumentData, Firestore, onSnapshot, updateDoc } from '@firebase/firestore';
import { defer, from } from 'rxjs';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import { ApiService, UserDetails } from 'shared/src/services/api.service';
import { AuthService } from './auth.service';
import { getPeechFirestore } from './peech-firestore';

// Reading-highlight color token, iOS HighlightColor.swift verbatim (declaration order is
// the picker's swatch order). An unrecognized Firestore value degrades to the default,
// mirroring the iOS decoder — never throws, never blocks the rest of the profile.
export const HIGHLIGHT_COLOR_TOKENS = ['pink', 'brown', 'purple', 'green', 'blue', 'grey'] as const;
export type HighlightColorToken = (typeof HIGHLIGHT_COLOR_TOKENS)[number];
export const DEFAULT_HIGHLIGHT_COLOR: HighlightColorToken = 'green';

// The *Dynamic custom property (shared/styles.scss) carrying a color's light/dark hex
// pair. One value per color, reused for the picker swatch, the reader's word wash and
// its playing-block accent alike (iOS HighlightColor.uiColor contract).
export function highlightColorCssVar(color: HighlightColorToken): string {
  return `--highlight${color.charAt(0).toUpperCase()}${color.slice(1)}Dynamic`;
}

function parseHighlightColor(value: unknown): HighlightColorToken {
  return (HIGHLIGHT_COLOR_TOKENS as readonly string[]).includes(value as string)
    ? (value as HighlightColorToken)
    : DEFAULT_HIGHLIGHT_COLOR;
}

// The identity fields of users/{uuid} in the mirror. avatarPath stores an absolute download
// URL (iOS uploads to Storage and writes url.absoluteString), so it is usable as <img src>.
export interface PeechUserProfile {
  name: string | null;
  email: string | null;
  avatarUrl: string | null;
  // Server-written personal forward-to address (iOS FirestoreUser.inboxEmail). The client
  // never derives it — absent means the feature is off for this user.
  inboxEmail: string | null;
  // Flat top-level fields on the same doc, iOS defaults: autoplay on, dyslexic font off.
  isAutoPlayEnabled: boolean;
  isDyslexicFontEnabled: boolean;
  // Backend-owned, set on user creation (iOS FirestoreUser.idleListeningTimeout, BAC-518):
  // SECONDS of listening without any player interaction before playback auto-pauses.
  // Absent means the feature is off for this user — there is no client default.
  idleListeningTimeout: number | null;

  highlightColor: HighlightColorToken;
}

// The boolean toggles routed through updatePreference() below. highlightColor and name
// are also client-writable, but each has its own dedicated method (updateHighlightColor,
// updateName) since neither is a boolean — keep this union narrow on purpose. Everything
// else on users/{uuid} is either backend-owned (subscription, credits, inboxEmail,
// idleListeningTimeout) or written by a dedicated service (pausedItem).
export type PeechUserPreference = 'isAutoPlayEnabled' | 'isDyslexicFontEnabled';

// Peech identity as an explicit state machine, so consumers can tell "signed out" apart
// from "details still loading" and "details failed" — and react to later transitions
// (sign-out in another tab, account switch) instead of latching the first value.
export type PeechIdentity =
  | { status: 'signedOut' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; details: UserDetails };

// Session-level Peech identity, shared so that header/library/etc. don't each repeat the
// GET /users/details call and the users/{uuid} doc listener.
@Injectable({
  providedIn: 'root',
})
export class PeechUserService {
  private readonly firestore: Firestore;

  // One cached GET /users/details per signed-in Firebase user. 'error' covers both the
  // 404 "no Peech account" case and transient failures — pages that must react to the 404
  // specifically (profile's Join Us modal) keep their own call.
  readonly identity$: Observable<PeechIdentity>;

  // Convenience view of identity$: the details when ready, null in every other state.
  readonly details$: Observable<UserDetails | null>;

  // Realtime identity from users/{uuid}; null while signed out or before details arrive.
  readonly profile$: Observable<PeechUserProfile | null>;

  // Re-fetches /users/details on demand: shareReplay would otherwise cache a transient
  // 'error' forever (distinctUntilChanged blocks a re-fetch for the same Firebase uid).
  private readonly refreshTrigger = new BehaviorSubject<void>(undefined);

  constructor(
    private api: ApiService,
    auth: AuthService
  ) {
    this.firestore = getPeechFirestore(auth.app);

    this.identity$ = auth.user$.pipe(
      distinctUntilChanged((previous, current) => previous?.uid === current?.uid),
      switchMap(user =>
        user
          ? this.refreshTrigger.pipe(
              switchMap(() =>
                this.api.getUserDetails().pipe(
                  map(details => ({ status: 'ready', details }) as PeechIdentity),
                  catchError(() => of<PeechIdentity>({ status: 'error' })),
                  startWith<PeechIdentity>({ status: 'loading' })
                )
              )
            )
          : of<PeechIdentity>({ status: 'signedOut' })
      ),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.details$ = this.identity$.pipe(
      map(identity => (identity.status === 'ready' ? identity.details : null))
    );

    this.profile$ = this.details$.pipe(
      switchMap(details => (details?.userId ? this.watchProfile(details.userId) : of(null))),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  // Retry after a transient /users/details failure (identity$ moves error → loading → …).
  refreshIdentity(): void {
    this.refreshTrigger.next();
  }

  // iOS FirestoreUserDataSource.updateHighlightColorSettings: same single-field, no-bump
  // contract as updatePreference above (see class comment there).
  updateHighlightColor(userId: string, color: HighlightColorToken): Observable<void> {
    return defer(() =>
      from(updateDoc(doc(this.firestore, 'users', userId.toLowerCase()), { highlightColor: color }))
    );
  }

  // iOS FirestoreUserDataSource.updateUserName: also routed through the generic
  // updateUserData (field "name", no timestamp bump) — there is no separate REST call.
  updateName(userId: string, name: string): Observable<void> {
    return defer(() =>
      from(updateDoc(doc(this.firestore, 'users', userId.toLowerCase()), { name }))
    );
  }

  // iOS FirestoreUserDataSource.updateUserData: a single-field updateDoc, no merge-set (a
  // blanket set would stomp backend-owned siblings), and deliberately no timestamp bump —
  // settings writes are not library activity.
  updatePreference(
    userId: string,
    preference: PeechUserPreference,
    value: boolean
  ): Observable<void> {
    return defer(() =>
      from(updateDoc(doc(this.firestore, 'users', userId.toLowerCase()), { [preference]: value }))
    );
  }

  private watchProfile(userId: string): Observable<PeechUserProfile | null> {
    return new Observable<PeechUserProfile | null>(subscriber => {
      const unsubscribe = onSnapshot(
        doc(this.firestore, 'users', userId.toLowerCase()),
        snapshot => subscriber.next(this.mapProfile(snapshot.data())),
        error => {
          // Consumers fall back to Firebase auth data — don't kill the stream.
          console.error('Failed to load user profile:', error);
          subscriber.next(null);
        }
      );

      return unsubscribe;
    });
  }

  private mapProfile(data: DocumentData | undefined): PeechUserProfile | null {
    if (!data) {
      return null;
    }

    const composedName = [data['firstName'], data['lastName']].filter(Boolean).join(' ');

    return {
      name: data['name'] || composedName || null,
      email: data['email'] ?? null,
      avatarUrl: data['avatarPath'] ?? null,
      inboxEmail: data['inboxEmail'] || null,
      isAutoPlayEnabled: data['isAutoPlayEnabled'] ?? true,
      isDyslexicFontEnabled: data['isDyslexicFontEnabled'] ?? false,
      idleListeningTimeout:
        typeof data['idleListeningTimeout'] === 'number' && data['idleListeningTimeout'] > 0
          ? data['idleListeningTimeout']
          : null,

      highlightColor: parseHighlightColor(data['highlightColor']),
    };
  }
}
